import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ContextMenuItemClickData } from '@em4cloud/my-cdk';
import { FolderType, ProcessFlatTreeNode, TypeOfProcessTreeNode } from '@em4cloud/process-tree';
import { Process } from 'app/api';
import { AlertService } from 'app/core/alert/alert.service';
import {
    ConfirmationCloseAction,
    ConfirmationService,
} from 'app/services/confirmation/confirmation.service';
import { OrgNavigationService } from 'app/services/org-nav.service';
import { switchMap } from 'rxjs';
import { ProcessService } from './process.service';

@Injectable({
    providedIn: 'root',
})
export class ProcessMenuService {
    constructor(
        private _processService: ProcessService,
        private _confirmationDialogService: ConfirmationService,
        private _matDialog: MatDialog,
        @Inject(DOCUMENT) private document: Document,
        private clipboard: Clipboard,
        private _alertService: AlertService,
        private orgNav: OrgNavigationService
    ) {}

    copyProcess(nodeId: string) {
        this._processService
            .copyBp(nodeId)
            .pipe(
                switchMap((response: string) => {
                    return this._processService.getBpls();
                })
            )
            .subscribe(data => {
                this._alertService.successAlert('bp', 'processCopiedSuccessMessage', 'copied');
            });
    }

    deleteProcess(nodeId: string) {
        const config = {
            title: 'deleteTitle',
            message: 'deleteMessage',
            actions: {
                confirm: {
                    label: 'delete',
                },
            },
        };
        this._confirmationDialogService
            .open(config)
            .afterClosed()
            .subscribe((action: ConfirmationCloseAction) => {
                if (action === 'confirmed') {
                    this._processService
                        .deleteBp(nodeId)
                        .pipe()
                        .subscribe(data => {
                            this._processService.initializeData();
                            this._alertService.successAlert(
                                'bp',
                                'processDeletedSuccessMessage',
                                'deleted'
                            );
                        });
                }
            });
    }

    private getTopProcess(process: Process): Process | null {
        // Top process
        if (!process.parentProcess) {
            return process;
        }

        const parentProcess = this._processService.state.find(p => p.id === process.parentProcess);
        if (!parentProcess) {
            return null;
        }

        return this.getTopProcess(parentProcess);
    }

    // adds a child process by default
    // pass new version as true to create a new version instead
    newProcess(node: ProcessFlatTreeNode, newVersion: boolean = false) {
        let internalId: string = node.folderType === FolderType.Area ? 'BA' : node.meta.internalId;
        const suffix = (node.childrenCount === undefined ? 0 : node.childrenCount) + 1;

        const parentProcess = this._processService.state.find(
            p =>
                p.id ===
                (node.type === TypeOfProcessTreeNode.Process ||
                node.type === TypeOfProcessTreeNode.SubProcess
                    ? node.id
                    : node.meta.process.id)
        );

        let version = +parentProcess.version;
        let wbs = parentProcess.wbs;
        if (newVersion) {
            const processes = this._processService.state.filter(p => p.wbs === parentProcess.wbs);

            version = processes.length + 1;
        } else {
            wbs = parentProcess.wbs + '.' + suffix;
        }

        internalId = internalId.split('-')[0]; // get internal id without version
        if (!newVersion) {
            internalId = internalId + '.' + suffix + '-1'; // add children suffix
        } else {
            internalId = internalId + '-' + version; // add version again
        }

        const topProcess = this.getTopProcess(parentProcess);

        let type;
        if (newVersion) {
            type = 'process';
        } else {
            type = topProcess ? 'subprocess' : 'process';
        }

        const process: Process = {
            internalId,
            wbs,
            version: `${version}`,
            area: parentProcess.area,
            owner: parentProcess.owner,
            ownerid: parentProcess.ownerid,
            status: 'DEVELOPMENT',
            auth: parentProcess.auth,
            organisation: parentProcess.organisation,
            parentProcess: newVersion ? null : node?.id,
            topProcess: newVersion ? null : topProcess?.id,
            type,
        };

        this._processService.openNewProcessDialog(process);
    }

    async newVersion(node: ProcessFlatTreeNode): Promise<void> {
        const parentProcess = this._processService.state.find(
            p =>
                p.id ===
                (node.type === TypeOfProcessTreeNode.Process ||
                node.type === TypeOfProcessTreeNode.SubProcess
                    ? node.id
                    : node.meta.process.id)
        );

        const newVersion = `${+parentProcess.version + 1}`;

        const newVersionProcess = await this._processService
            .generateNewVersion(parentProcess.id, newVersion)
            .toPromise();
        await this._processService.getBpls().toPromise();

        this._alertService.successAlert('bp', 'newVersion', 'success');

        this.orgNav.navigate([], { queryParams: { bpId: newVersionProcess.id } });
    }

    shareProcess(link: string) {
        this.clipboard.copy(this.document.location.host + link);
        this._alertService.basicAlert('actions', 'linkCopiedClipboard');
    }

    menuItemClicked($event: ContextMenuItemClickData<ProcessFlatTreeNode | any>) {
        if ($event.menuItem.id === 'copy') this.copyProcess($event.node.id);
        else if ($event.menuItem.id === 'delete') this.deleteProcess($event.node.id);
        else if ($event.menuItem.id === 'addChild') this.newProcess($event.node);
        else if ($event.menuItem.id === 'addVersion') this.newVersion($event.node);
        else if ($event.menuItem.id === 'shareLink') {
            this.shareProcess(this.buildNodeRelativeLink($event.node as ProcessFlatTreeNode));
        }
    }

    buildNodeRelativeLink(node: ProcessFlatTreeNode | any): string {
        let ids: string[] = [];
        let id: string = '';
        if (
            !node.type ||
            node.type === TypeOfProcessTreeNode.Process ||
            node.type === TypeOfProcessTreeNode.SubProcess ||
            node.type === TypeOfProcessTreeNode.ConfigurationProcess
        ) {
            // ids.push('bpId=' + node.id);
            id = `${node.id}`;
            ids.push('wbs=' + node.meta.wbs);
        } else if (node.type === TypeOfProcessTreeNode.Scenario) {
            ids.push('tscId=' + node.id);
        }
        let path = '?' + ids.join('&');
        const baseUrl = this.orgNav.url.split(/[?#;]/)[0];
        const urls = baseUrl.split('/');
        if (urls.length >= 4) {
            urls[4] = id;
        } else {
            urls.push(id);
        }
        const finalBaseUrl = urls.join('/');
        const finalUrl = finalBaseUrl + path;
        return finalUrl;
    }
}
